import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";


import { FileDownIcon, PackageIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { ExpiryLotRow } from "../../domain/repo/get-expiry-lots";
import type { WastageReportItem } from "../../domain/repo/get-wastage-report";
import type { ExpiryLotsParams, InventoryParams } from "../api/client";
import { reportsApi } from "../api/client";
import { useInventorySummaryQuery } from "../api/queries";
import { InventoryReportsContent } from "../ui/inventory/InventoryReportsContent";
import { InventoryReportsSummaryCards } from "../ui/inventory/InventoryReportsSummaryCards";

function exportInventoryToCsv(
  expiryLots: ExpiryLotRow[],
  wastage: WastageReportItem[],
  groupBy: "day" | "week" | "month",
) {
  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case "INGREDIENT":
        return "ວັດຖຸດິບ";
      case "RETAIL_VARIANT":
        return "ສິນຄ້າສຳເລັດຮູບ";
      default:
        return type;
    }
  };

  // Expiry Lots section
  const expiryHeaders = [
    "Lot No",
    "ສາຂາ",
    "ສິນຄ້າ",
    "ປະເພດ",
    "ວັນທີຫມົດອາຍຸ",
    "ວັນທີຮັບ",
    "ຈຳນວນຄົງເຫຼືອ",
    "ມູນຄ່າ (LAK)",
  ];

  const expiryRows = expiryLots.map((item) => {
    // Handle expiryDate - could be Date or string
    const expiryDateObj =
      item.expiryDate instanceof Date
        ? item.expiryDate
        : item.expiryDate
          ? new Date(item.expiryDate)
          : null;
    const expiryDateStr = expiryDateObj
      ? expiryDateObj.toISOString().split("T")[0]
      : "";

    // Handle receivedAt - could be Date or string
    const receivedDateObj =
      item.receivedAt instanceof Date
        ? item.receivedAt
        : new Date(item.receivedAt);
    const receivedDateStr = receivedDateObj.toISOString().split("T")[0];

    const valueLak =
      item.costPerUnitLak && item.qtyRemaining
        ? item.costPerUnitLak * item.qtyRemaining
        : 0;

    return [
      item.lotNo,
      item.branchName || "",
      item.itemName || "",
      getItemTypeLabel(item.itemType),
      expiryDateStr,
      receivedDateStr,
      String(item.qtyRemaining.toFixed(2)),
      String(valueLak),
    ];
  });

  // Wastage section
  const wastageHeaders = [
    groupBy === "month" ? "ເດືອນ" : groupBy === "week" ? "ອາທິດ" : "ວັນທີ",
    "ສາຂາ",
    "ສິນຄ້າ",
    "ປະເພດ",
    "ຈຳນວນຂອງເສຍ",
    "ຄ່າໃຊ້ຈ່າຍ (LAK)",
  ];

  const wastageRows = wastage.map((item) => [
    item.period,
    item.branchName || "",
    item.itemName || "",
    item.itemType ? getItemTypeLabel(item.itemType) : "",
    String(item.totalQtyWasted.toFixed(2)),
    String(item.totalCostLak),
  ]);

  // Combine sections with empty line separator
  const csvContent = [
    "=== Lot ໃກ້ຫມົດອາຍຸ ===",
    expiryHeaders.join(","),
    ...expiryRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
    "",
    "=== ຂອງເສຍ ===",
    wastageHeaders.join(","),
    ...wastageRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10);
  a.download = `inventory-report-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function InventoryReportsPage() {
  // Default filters - use useMemo to prevent infinite loops
  const defaultFilters = useMemo(() => {
    return {
      daysBeforeExpiry: 30, // Default: show lots expiring within 30 days
      groupBy: "day" as const,
    };
  }, []);

  const [filters, setFilters] = useState<InventoryParams>(defaultFilters);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const { data: summaryData, isLoading: isLoadingSummary } =
    useInventorySummaryQuery(filters);

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    setOffset(newOffset);
    setLimit(newLimit);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportParams: ExpiryLotsParams & InventoryParams = {
        ...filters,
        limit: 10000, // High limit for export
        offset: 0,
      };
      const result = await reportsApi.getInventoryExport(exportParams);

      if (result.expiryLots.length === 0 && result.wastage.length === 0) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }

      exportInventoryToCsv(
        result.expiryLots,
        result.wastage,
        filters.groupBy || "day",
      );
      const totalItems = result.expiryLots.length + result.wastage.length;
      toast.success(`ສົ່ງອອກ ${totalItems.toLocaleString("lo-LA")} ລາຍການແລ້ວ`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍງານສາງ",
              to: "/app/reports/inventory",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <PackageIcon className="size-5" />
              ລາຍງານສາງ
            </h2>
            <p className="text-muted-foreground">
              ຕິດຕາມ Lot ໃກ້ຫມົດອາຍຸ ແລະ ຂອງເສຍ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <FileDownIcon className="size-4" />
            {isExporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ (CSV)"}
          </Button>
        </div>

        {/* <InventoryReportsFilter
          filters={filters}
          onFiltersChange={setFilters}
        /> */}

        <div className="mb-4">
          <InventoryReportsSummaryCards
            totalExpiringLots={summaryData?.totalExpiringLots ?? 0}
            totalExpiringValueLak={summaryData?.totalExpiringValueLak ?? 0}
            totalWastageQty={summaryData?.totalWastageQty ?? 0}
            totalWastageCostLak={summaryData?.totalWastageCostLak ?? 0}
            avgWastagePerDay={summaryData?.avgWastagePerDay ?? 0}
            isLoading={isLoadingSummary}
          />
        </div>

        <InventoryReportsContent
          filters={filters}
          onPaginationChange={handlePaginationChange}
          limit={limit}
          offset={offset}
        />
      </Main>
    </>
  );
}
